import { Column, ErrorPlaceholder, LoadingIndicator, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

import { useGetUserPushPreferences } from "@/hooks/queries/useGetUserPushPreferences";
import { useUpdateSetUserPushPreference } from "@/hooks/updateSets/useUpdateSetUserPushPreference";
import {
  mobilePushSectionMeta,
  PUSH_MODES_IN_ORDER,
  PUSH_TYPES_IN_ORDER,
  pushModeToLabel,
  pushTypeToSettingsMeta,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { PushMode, PushType } from "@/types";

export const MobilePushSettingsSection = ({
  onPreferenceChange,
}: {
  onPreferenceChange: () => void;
}) => {
  const {
    data: preferences = [],
    isPending,
    isError,
  } = useGetUserPushPreferences();
  const { Icon, className } = mobilePushSectionMeta;

  const typeToModeMap = new Map(
    preferences.map((preference) => [preference.type, preference.mode]),
  );

  const renderRows = () => {
    if (isPending) {
      return <LoadingIndicator />;
    }
    if (isError) {
      return <ErrorPlaceholder />;
    }
    return PUSH_TYPES_IN_ORDER.map((type, index) => {
      const mode = typeToModeMap.get(type);
      if (!mode) return null;
      return (
        <MobilePushSettingsTypeRow
          key={type}
          hasTopBorder={index > 0}
          mode={mode}
          onPreferenceChange={onPreferenceChange}
          type={type}
        />
      );
    });
  };

  const render = () => (
    <Row className="items-start gap-16 py-16">
      <Column className="w-[240px] shrink-0">
        <Row className="items-start gap-5 py-4">
          <span
            className={cn(
              "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
              className,
            )}
          >
            <Icon className="h-4 w-4" />
          </span>
          <Column className="items-start gap-1">
            <Label className="text-base font-semibold text-slate-900">
              {mobilePushSectionMeta.label}
            </Label>
            <Text className="text-sm font-normal leading-relaxed text-slate-500">
              {mobilePushSectionMeta.description}
            </Text>
          </Column>
        </Row>
      </Column>

      <Column className="w-full max-w-xl gap-0">{renderRows()}</Column>
    </Row>
  );

  return render();
};

const MobilePushSettingsTypeRow = ({
  hasTopBorder,
  mode,
  onPreferenceChange,
  type,
}: {
  hasTopBorder: boolean;
  mode: PushMode;
  onPreferenceChange: () => void;
  type: PushType;
}) => {
  const { mutate: updatePreference } = useUpdateSetUserPushPreference();
  const settingsMeta = pushTypeToSettingsMeta[type];

  const handleSelectMode = (nextMode: PushMode) => {
    if (nextMode === mode || settingsMeta?.isRequired) return;
    mixpanelUtils.trackButtonClick(
      "Set mobile push mode",
      MIXPANEL_SCREENS.NOTIFICATIONS,
      { mode: nextMode, push_type: type },
    );
    updatePreference({ type, mode: nextMode });
    onPreferenceChange();
  };

  if (!settingsMeta) {
    return null;
  }

  const render = () => (
    <Row
      className={cn(
        "min-h-[80px] items-center justify-between gap-6 py-6",
        hasTopBorder && "border-t border-slate-50",
      )}
    >
      <Column className="min-w-0 flex-1 gap-1">
        <Text className="text-sm font-normal text-slate-700">
          {settingsMeta.label}
        </Text>
        <Row className="flex-wrap items-center gap-2">
          {settingsMeta.isRequired ? (
            <span className="rounded-md border border-slate-400 px-1.5 py-0.5 text-xs font-medium text-slate-600">
              Required
            </span>
          ) : null}
          {settingsMeta.note ? (
            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600">
              {settingsMeta.note}
            </span>
          ) : null}
        </Row>
      </Column>
      <Row className="items-center gap-2">
        {(settingsMeta.modes ?? PUSH_MODES_IN_ORDER).map((option) => {
          const isSelected = option === mode;
          return (
            <Button
              key={option}
              className={cn(
                "h-9 min-w-[72px] rounded-full border px-4 text-sm font-medium",
                isSelected
                  ? "border-primary-500 bg-primary-100 font-semibold text-primary-900 hover:bg-primary-200"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                settingsMeta.isRequired && "cursor-not-allowed opacity-80",
              )}
              disabled={settingsMeta.isRequired}
              onClick={() => {
                handleSelectMode(option);
              }}
              variant="outlineSecondary"
            >
              {pushModeToLabel[option]}
            </Button>
          );
        })}
      </Row>
    </Row>
  );

  return render();
};
